/**
 * Model catalog for the Excel multi-hop executor.
 *
 * Derived from the shared internal-picker catalog so PowerPoint and Excel use
 * the same OpenAI ids and capability metadata. Excel additionally consumes the
 * catalog's Vertex Claude entries.
 */

import type { AIProvider } from "../../shared/config/models";
import {
  getModelPickerEntries,
  type ModelThinkingEffort,
  type OpenAIReasoningMode,
} from "../../shared/modelPicker/catalog";

export type ExecutorReasoningEffort = ModelThinkingEffort;

export interface ExecutorModelConfig {
  /** Stable selector used by `resolveExecutorModel` and diagnostics. */
  id: string;
  label: string;
  provider: AIProvider;
  /** Provider-specific wire model id. */
  model: string;
  reasoningEffort: ExecutorReasoningEffort;
  /** OpenAI-only; omitted for Claude and models without mode support. */
  reasoningMode?: OpenAIReasoningMode;
  /** Overrides the adapter's default output cap. */
  maxOutputTokens?: number;
  forcedToolChoice: "supported" | "auto-only";
}

const PRODUCTION_EFFORT_OVERRIDES: Readonly<
  Partial<Record<string, ExecutorReasoningEffort>>
> = {
  "gpt-5.5": "low",
  "opus-5": "low",
};

export const EXECUTOR_MODELS: readonly ExecutorModelConfig[] =
  getModelPickerEntries("excel").map((entry) => ({
    id: entry.id,
    label: entry.label,
    provider: entry.provider as AIProvider,
    model: entry.model,
    reasoningEffort: PRODUCTION_EFFORT_OVERRIDES[entry.id] ?? entry.defaultEffort,
    ...(entry.allowedModes?.includes("pro")
      ? { reasoningMode: entry.defaultMode ?? "standard" }
      : {}),
    ...(entry.maxOutputTokens ? { maxOutputTokens: entry.maxOutputTokens } : {}),
    forcedToolChoice: entry.forcedToolChoice,
  }));

/**
 * Model the executor runs on unless a caller names another entry.
 *
 * Which model production runs is no longer an edit to this constant: it is the
 * `excelUseClaudeOpus` ConfigCat flag, read once per run by
 * `resolveEffectiveExcelExecutorModel`.
 * What this constant fixes is where every unresolved flag state lands. Keep
 * prior registry entries available so a future model router can select them
 * without another provider-surface migration.
 */
export const DEFAULT_EXECUTOR_MODEL_ID = "gpt-6-astra";

/** Model served when `excelUseClaudeOpus` is enabled for the current user. */
export const FLAGGED_EXECUTOR_MODEL_ID = "opus-5";

/**
 * Resolves a model id to its config, falling back to the default entry.
 *
 * The optional `id` lets a caller pin a specific model (tests select the
 * non-default provider this way). Resolution never throws on an unknown id: a
 * stale selector degrades to the default rather than failing the run.
 */
export function resolveExecutorModel(id?: string | null): ExecutorModelConfig {
  const requested = id?.trim();
  const match = requested
    ? EXECUTOR_MODELS.find((entry) => entry.id === requested)
    : undefined;
  if (match) return match;
  const fallback = EXECUTOR_MODELS.find(
    (entry) => entry.id === DEFAULT_EXECUTOR_MODEL_ID,
  );
  if (!fallback) {
    throw new Error(
      `Executor model registry is missing its default entry "${DEFAULT_EXECUTOR_MODEL_ID}"`,
    );
  }
  return fallback;
}
